/* Pump Company Tracker — public no-login page for a concrete-pump contractor.
   URL: /pump/:token — shows today's jobs where this pump is on-site, plus live
   truck positions + ETAs so the pump operator knows what's coming. */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ContractorFleetMap } from "@/components/contractor/ContractorFleetMap";
import axios from "axios";
import { Truck, MapPin, Clock, HardHat, RefreshCcw, CheckCircle2, Phone, QrCode, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import useLiveRefresh from "@/hooks/useLiveRefresh";
import ClaimPumpAccount from "@/components/ClaimPumpAccount";

import { BACKEND_ORIGIN } from "@/lib/api";
const BACKEND = BACKEND_ORIGIN;
const API = `${BACKEND}/api`;

const statusColor = (s) => ({
  scheduled:   "text-sky-500 border-sky-500/40 bg-sky-500/10",
  in_progress: "text-primary-orange border-primary-orange/50 bg-primary-orange/10",
  completed:   "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
}[s] || "text-muted-industrial border-industrial");

const truckStatusLabel = (s) => ({
  available: "At plant", loading: "Loading", en_route: "En route",
  on_site: "On site", returning: "Returning", maintenance: "Service",
}[s] || s || "—");

export default function PumpTracker({ mode = "pump" }) {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const endpoint = mode === "job" ? "job" : mode === "unit" ? "pump-unit" : "pump";
  const [data, setData] = useState(null);
  // Job Linker — which pour this pump crew is on. URL ?job=<id> (from a share link / QR) wins,
  // then the last choice remembered on this device, otherwise the dropdown asks.
  const storageKey = `pump_job_${token}`;
  const [jobId, setJobId] = useState(() => params.get("job") || localStorage.getItem(`pump_job_${token}`) || "");
  const pickJob = (id) => {
    setJobId(id);
    if (id) localStorage.setItem(storageKey, id); else localStorage.removeItem(storageKey);
    const next = new URLSearchParams(params);
    if (id) next.set("job", id); else next.delete("job");
    setParams(next, { replace: true });
  };
  const [error, setError] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const isDemo = !!data?.supplier?.is_demo;
  useLiveRefresh(() => load(), { intervalMs: isDemo ? 5000 : 30_000, org: isDemo ? data?.supplier?.org_id : null });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/${endpoint}/${token}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "Invalid tracking link" : "Cannot reach server");
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[rgb(var(--bg))]" data-testid="pump-tracker-error">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
            <HardHat className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="heading text-2xl font-black uppercase mb-2">{error}</h1>
          <p className="text-muted-industrial text-sm">Contact your supplier to reissue your pump tracker link.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--bg))] label-tag text-primary-orange pulse-orange">Connecting…</div>;
  }

  const linkedJob = jobId && data.jobs.find((j) => j.job_id === jobId);
  const visibleJobs = mode === "job" ? data.jobs : linkedJob ? [linkedJob] : data.jobs;
  const showLinker = mode === "pump" && data.jobs.length > 0;
  const mapTrucks = visibleJobs.flatMap((j) => j.trucks.filter((t) => t.lat != null && t.lng != null).map((t) => ({
    id: t.truck_id, number: t.number, status: t.status, lat: t.lat, lng: t.lng,
    job: { id: j.job_id, site_lat: j.site_lat, site_lng: j.site_lng, site_name: j.site_name || j.site_address },
  })));
  const hasSites = visibleJobs.some((j) => j.site_lat != null && j.site_lng != null);

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-14" data-testid="pump-tracker">
      <div className="bg-surface border-b-2 border-primary-orange sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-orange flex items-center justify-center shadow-md">
              <HardHat className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              {/* This page belongs to the PUMP company, not the plant — lead with the
                  ArmorBatch mark and name the supplier whose loads they're watching. */}
              <div className="heading text-lg font-black leading-none" data-testid="tracker-supplier">
                ARMOR<span className="text-primary-orange">BATCH</span> GLOBAL
              </div>
              <div className="label-tag text-primary-orange">
                {mode === "job" ? "PUMP OPERATOR · JOB TRACKER" : mode === "unit" ? "MY PUMP · LIVE TRUCKS" : "PUMP TRACKER"}
                {data.supplier?.brand || data.supplier?.name ? ` · LOADS FROM ${(data.supplier.brand || data.supplier.name).toUpperCase()}` : ""}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {isDemo && <span data-testid="tracker-live-sync" className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-sky-500 text-black font-bold">● live sync</span>}
              <button type="button" data-testid="tracker-qr-btn" onClick={() => setShowQr((v) => !v)} title="Generate QR for this tracking page"
                className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black">
                <QrCode className="w-3.5 h-3.5" /> Generate QR
              </button>
            </div>
          </div>
          <button onClick={load} className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1" data-testid="pump-tracker-refresh">
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {mode === "pump" && <ClaimPumpAccount token={token} pumpName={data.pump_company?.name} />}

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div>
          {showQr && (
            <div data-testid="tracker-qr-panel" className="mb-4 border border-primary-orange bg-surface p-4 flex flex-col sm:flex-row items-center gap-4">
              <div className="p-3 bg-white rounded-lg"><QRCodeSVG value={window.location.href} size={160} level="M" /></div>
              <div className="min-w-0 flex-1">
                <div className="label-tag">SHARE THIS LIVE TRACKER</div>
                <p className="text-sm text-muted-industrial mt-1">Pump crew or foreman scans this to follow every truck on the pour — no login, no app.{linkedJob ? " This code opens straight on your selected job — no dropdown needed." : ""}</p>
                <div className="mono text-[10px] break-all mt-2 border border-industrial px-2 py-1" data-testid="tracker-qr-url">{window.location.href}</div>
              </div>
              <button type="button" data-testid="tracker-qr-close" onClick={() => setShowQr(false)} className="self-start p-1 text-muted-industrial hover:text-ink"><X className="w-4 h-4" /></button>
            </div>
          )}
          <div className="label-tag">{mode === "job" ? "SITE" : mode === "unit" ? "MY PUMP" : "PUMP CONTRACTOR"}</div>
          <h1 className="heading text-2xl font-black uppercase tracking-tight" data-testid="pump-tracker-name">
            {mode === "job" ? (data.job_share?.site_name || data.job_share?.site_address || "Job tracker") : data.pump_company?.name}
          </h1>
          <div className="text-xs text-muted-industrial mt-1">
            Jobs for {data.date} · Updated {new Date(data.server_time).toLocaleTimeString()}
          </div>
        </div>

        {showLinker && (
          <div className="card-v2 p-4" data-testid="pump-job-linker">
            <label className="label-tag block mb-1.5" htmlFor="pump-job-select">Select Your Current Job</label>
            <select
              id="pump-job-select"
              data-testid="pump-job-select"
              value={linkedJob ? jobId : ""}
              onChange={(e) => pickJob(e.target.value)}
              className="w-full h-12 px-3 bg-surface-2 border border-industrial text-ink mono text-sm focus:border-primary-orange outline-none"
            >
              <option value="">All jobs today ({data.jobs.length}) — pick your pour to filter</option>
              {data.jobs.map((j) => (
                <option key={j.job_id} value={j.job_id}>
                  {j.time_required || "TBA"} · {j.customer_name} · {j.site_name || j.site_address || "Site TBA"} · {j.quantity_m3} m³
                </option>
              ))}
            </select>
            <div className="mt-1.5 text-[11px] text-muted-industrial mono" data-testid="pump-job-linker-hint">
              {linkedJob
                ? <>Showing only trucks for <span className="text-primary-orange">{linkedJob.site_name || linkedJob.site_address}</span>. <button type="button" onClick={() => pickJob("")} className="underline hover:text-ink" data-testid="pump-job-clear">Show all</button></>
                : "Each pump crew picks its own pour — you'll only see the trucks heading to your site."}
            </div>
          </div>
        )}

        {(mapTrucks.length > 0 || hasSites) && (
          <div className="card-v2 overflow-hidden" data-testid="pump-map">
            <div className="h-64 sm:h-80">
              <ContractorFleetMap trucks={mapTrucks} plants={[]} selectedId={null} onSelect={() => {}} />
            </div>
            <div className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-industrial flex justify-between">
              <span data-testid="pump-map-count">{mapTrucks.length} truck{mapTrucks.length === 1 ? "" : "s"} on map</span>
              <span>{linkedJob ? linkedJob.site_name || linkedJob.site_address : `${visibleJobs.length} sites`}</span>
            </div>
          </div>
        )}

        {data.jobs.length === 0 && (
          <div className="card-v2 p-8 text-center" data-testid="no-jobs">
            <Truck className="w-8 h-8 text-muted-industrial mx-auto mb-3" />
            <div className="heading text-lg font-bold uppercase">No jobs assigned today</div>
            <div className="text-sm text-muted-industrial mt-1">
              Contact your concrete supplier if you're expecting a pour — they'll link your pump to the job.
            </div>
          </div>
        )}

        {visibleJobs.map((j) => (
          <div key={j.job_id} data-testid={`pump-job-${j.job_id}`} className="card-v2 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">
                  {j.time_required || "TBA"} · {j.customer_name}
                </div>
                <div className="heading font-black text-lg truncate">{j.site_name || "Site TBA"}</div>
                <div className="text-xs text-muted-industrial truncate mt-0.5">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {j.site_address || "—"}
                </div>
              </div>
              <span className={`mono text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-md ${statusColor(j.status)} shrink-0`}>
                {j.status?.replace("_", " ")}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-xs border-t border-industrial pt-3">
              <div><div className="label-tag">Mix</div><div className="mono">{j.mix_code}</div></div>
              <div><div className="label-tag">Ordered</div><div className="mono">{j.quantity_m3} m³</div></div>
              <div><div className="label-tag">Delivered</div><div className="mono text-primary-orange">{j.delivered_m3} / {j.quantity_m3} m³</div></div>
            </div>

            <div className="mt-3 h-2 bg-surface-2 rounded-full overflow-hidden">
              <div className="h-full bg-primary-orange transition-all"
                   style={{ width: `${Math.min(100, (j.delivered_m3 / Math.max(1, j.quantity_m3)) * 100)}%` }} />
            </div>

            {j.trucks.length > 0 ? (
              <div className="mt-4 space-y-2">
                <div className="label-tag">Trucks inbound</div>
                {j.trucks.map((t) => (
                  <div key={t.truck_id} className="border border-industrial rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`pump-truck-${t.number}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary-orange/15 border border-primary-orange/30 flex items-center justify-center shrink-0">
                        <Truck className="w-4 h-4 text-primary-orange" />
                      </div>
                      <div className="min-w-0">
                        <div className="heading font-black text-sm">{t.number}</div>
                        <div className="text-[11px] mono text-muted-industrial">{truckStatusLabel(t.status)}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {t.eta_min != null ? (
                        <>
                          <div className="mono text-lg font-bold text-primary-orange leading-none inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {t.eta_min} min
                          </div>
                          <div className="text-[10px] mono text-muted-industrial mt-1">{t.distance_km} km away</div>
                        </>
                      ) : t.status === "on_site" ? (
                        <div className="mono text-sm font-bold text-emerald-500 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> On site
                        </div>
                      ) : (
                        <div className="text-[11px] mono text-muted-industrial">Locating…</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : j.status !== "completed" && (
              <div className="mt-4 text-xs text-muted-industrial mono border border-dashed border-industrial rounded-lg p-3 text-center">
                Truck not yet assigned — check back shortly.
              </div>
            )}
          </div>
        ))}

        <div className="text-center pt-6 label-tag">© ARMORBATCH GLOBAL · Pump Tracker</div>
      </div>
    </div>
  );
}
